// PrefsEditModal — in-workspace editor for the seeded preferences.
//
// Why a separate component (not reuse Auth.tsx's PreferencesScreen):
//   - PreferencesScreen is wrapped in <Shell> + <AsideRole>, both of
//     which assume the auth-flow chrome (logo, side panel, page-level
//     scroll lock). Embedding it in a modal would force a mountain of
//     conditional layout flags into Auth.tsx.
//   - The option keys (BUDGET_BUCKETS / TIMELINE_OPTS / PRIORITY_OPTS)
//     ARE shared via prefs-options.ts, and the preferred-areas picker is
//     the shared <AreaMultiSelect> (regions + CBD + 28 districts). The
//     component is thin chrome over the same option model.
//
// Behavior:
//   - Esc / backdrop-click / × closes without saving.
//   - "Save" PATCHes /workspaces/{id} with the diff and calls onSaved.
//   - Best-effort: a failure shows an inline error and keeps the modal
//     open so the user can retry.

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { apiPatchJson } from "@/lib/api";
import { STORAGE_EVENTS } from "@/constants/storage";
import {
  BUDGET_BUCKETS, BUDGET_KEYS,
  PRIORITY_OPTS, TIMELINE_OPTS, bucketFromRange,
} from "@/lib/prefs-options";
import { AreaMultiSelect } from "@/components/AreaMultiSelect";

interface InitialPrefs {
  budget_min: number | null;
  budget_max: number | null;
  areas: string;
  timeline: string;
  priorities: string[];
}

export function PrefsEditModal({
  workspaceId,
  initial,
  onClose,
  onSaved,
}: {
  workspaceId: string;
  initial: InitialPrefs;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [budget, setBudget] = useState<string>(() => bucketFromRange(initial.budget_min, initial.budget_max));
  const [areas, setAreas] = useState<string[]>(
    () => (initial.areas || "").split(",").map((a) => a.trim()).filter(Boolean)
  );
  const [timeline, setTimeline] = useState<string>(initial.timeline ?? "");
  const [priorities, setPriorities] = useState<Set<string>>(
    () => new Set(Array.isArray(initial.priorities) ? initial.priorities : [])
  );
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // ARIA APG: aria-modal dialogs MUST handle Escape.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.stopPropagation(); onClose(); }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const toggle = (s: Set<string>, v: string) => {
    const next = new Set(s);
    if (next.has(v)) next.delete(v); else next.add(v);
    return next;
  };

  const save = async () => {
    setSaving(true);
    setErr(null);
    const bucket = budget ? BUDGET_BUCKETS[budget] : null;
    const patch = {
      budget_min: bucket?.min ?? null,
      budget_max: bucket?.max ?? null,
      areas: areas.join(","),
      timeline,
      priorities: Array.from(priorities),
    };
    try {
      await apiPatchJson(`/workspaces/${workspaceId}`, patch);
      onSaved();
      // The backend kicks off an async seed-shortlist goroutine when prefs
      // change. The first refresh picks up the prefs themselves; the
      // delayed second refresh picks up the seeded properties (which
      // typically land within ~1-2s once dataengine responds). Without
      // this, a fresh user would see empty boards until they manually
      // reload the page.
      window.dispatchEvent(new CustomEvent(STORAGE_EVENTS.workspaceRefresh));
      window.setTimeout(() => {
        window.dispatchEvent(new CustomEvent(STORAGE_EVENTS.workspaceRefresh));
      }, 2500);
      onClose();
    } catch {
      setErr(t("workspace.editPrefs.saveError"));
      setSaving(false);
    }
  };

  if (typeof document === "undefined") return null;

  const Section = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className="mb-4">
      <p className="mb-2 text-[11px] font-semibold uppercase tracking-widest text-text-muted">{label}</p>
      <div className="flex flex-wrap gap-1.5">{children}</div>
    </div>
  );
  const Chip = ({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) => (
    <button
      type="button"
      onClick={onClick}
      className={
        "rounded-full border px-2.5 py-1 text-[12px] transition-colors " +
        (active
          // Light-surface chip: emerald-50 fill + 900 ink, matches the
          // banner palette in WorkspacePrefsBanner.
          ? "border-emerald-500 bg-emerald-50 text-emerald-900 font-medium"
          : "border-border bg-surface-2 text-text-sec hover:bg-surface-3")
      }
    >
      {children}
    </button>
  );

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("workspace.editPrefs.title")}
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(0,0,0,0.55)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="bg-surface text-text rounded-xl border border-border"
        style={{ maxWidth: 540, width: "100%", padding: "20px 22px", boxShadow: "0 24px 64px rgba(0,0,0,0.4)" }}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="m-0 text-[17px] font-semibold">{t("workspace.editPrefs.title")}</h2>
          <button
            onClick={onClose}
            aria-label={t("common.close")}
            className="rounded p-1 text-text-muted hover:bg-surface-2"
            style={{ fontSize: 18, lineHeight: 1 }}
          >×</button>
        </div>

        <Section label={t("auth.prefs.budget")}>
          {BUDGET_KEYS.map((k) => (
            <Chip key={k} active={budget === k} onClick={() => setBudget(budget === k ? "" : k)}>
              {t(`auth.prefs.budgets.${k}`)}
            </Chip>
          ))}
        </Section>
        <div className="mb-4">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-widest text-text-muted">
            {t("auth.prefs.areas")}
          </p>
          <AreaMultiSelect value={areas} onChange={setAreas} />
        </div>
        <Section label={t("auth.prefs.timeline")}>
          {TIMELINE_OPTS.map((k) => (
            <Chip key={k} active={timeline === k} onClick={() => setTimeline(timeline === k ? "" : k)}>
              {t(`auth.prefs.timelineOpts.${k}`)}
            </Chip>
          ))}
        </Section>
        <Section label={t("auth.prefs.priorities")}>
          {PRIORITY_OPTS.map((k) => (
            <Chip key={k} active={priorities.has(k)} onClick={() => setPriorities((s) => toggle(s, k))}>
              {t(`auth.prefs.priorityOpts.${k}`)}
            </Chip>
          ))}
        </Section>

        {err && (
          <div className="mb-3 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-[12px] text-red-200">
            {err}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button
            onClick={onClose}
            className="rounded-md border border-border bg-surface-2 px-3 py-1.5 text-[12px] text-text-sec hover:bg-surface-3"
          >
            {t("common.cancel")}
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="rounded-md bg-emerald-500 px-3.5 py-1.5 text-[12px] font-semibold text-white disabled:opacity-50"
          >
            {saving ? t("workspace.editPrefs.saving") : t("workspace.editPrefs.save")}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
